import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { BookmarkComponent } from '@core/components/bookmark/bookmark.component';
import { PipesModule } from '@core/pipes/pipes.module';
import { FilesService, SchoolsService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { IconComponent } from '@shared/icon/icon.component';
import { RatingComponent } from '@shared/rating/rating.component';
import { getMe, getUserLanguages } from '@store/reducers/profile.reducer';

import {
  CSchool,
  CSchoolParticipant,
  ESchoolParticipantRole,
  IContentPane,
  ImageSizeEnum,
  Subject,
  User,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-school',
  standalone: true,
  imports: [
    LocaleSharedModule,
    BookmarkComponent,
    CommonModule,
    PipesModule,
    OnUiCoverModule,
    RatingComponent,
    IconComponent,
  ],
  templateUrl: './school.component.html',
  styleUrls: ['./school.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchoolComponent extends ChangableComponent implements OnInit {
  deviceService = inject(DeviceDetectorService);
  private store = inject(Store);
  router = inject(Router);

  @Input() view: string;
  @Input()
  public set school(school: CSchool) {
    this._school = school;
    this.prepareCard();
  }

  public get school(): CSchool {
    return this._school;
  }

  @Output() public openInModal = new EventEmitter<CSchool>();

  public title: string;
  public description: string;
  public coverImg: OnUiCover;
  public participants: CSchoolParticipant[] = [];
  public user: User;
  public subjects: Subject[] = [];
  public schoolUrl: string;
  public fullSchoolUrl: string;
  public schoolForm = false;
  public alertOpened = false;
  public copied = false;
  public subjectId: number;
  public originalUrl: string;
  public doHover = false;

  private _school: CSchool;
  private _me: User;
  private _language: string;

  public ngOnInit(): void {
    this.store
      .select(getMe)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (me) => {
          this._me = me;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-USER');
          this.detectChanges();
        },
      });

    this.store
      .select(getUserLanguages)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this._language = res[0];
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public get me(): User {
    return this._me;
  }

  public getSubjectTitle(subject: Subject) {
    if (this._language === 'ru') {
      return subject['title_ru'];
    } else {
      return subject['title_en'];
    }
  }

  public subjectsTrackByFn(index, subject: Subject): number {
    return subject.id;
  }

  public trackByFn(index: number, item: IContentPane): number {
    return item.order;
  }

  public get meIsOwnerSchool(): boolean {
    return !!this._school?.participants
      .filter((_p) => _p.role === ESchoolParticipantRole.owner)
      .find((_part) => _part.account_id === this._me?.id);
  }

  public onToggleForm() {
    this.schoolForm = !this.schoolForm;
    this.markForCheck();
  }

  public changed(event: boolean) {
    if (event) {
      this.onToggleForm();
    }
  }

  public onCopied(event: any) {
    this.copied = true;
    this.markForCheck();

    this.setTimeout(() => {
      this.copied = false;
      this.markForCheck();
    }, 1000);
  }

  public onOpenCardInModal() {
    this.openInModal.emit(this.school);
  }

  private prepareCard() {
    this.title = this._school?.title;
    this.description = this._school.description;
    this.participants = this._school?.participants;
    this.subjects = this._school?.subjects;
    this.schoolUrl = SchoolsService.schoolRoute(this._school, false).join('/');
    this.fullSchoolUrl = SchoolsService.schoolRoute(this._school).join('/');
    this.coverImg = {
      type: 'cover',
      img: this._school.__opts?.images[0]?.widex2
        ? this.prepareStorageUrl(this._school.__opts?.images[0]?.widex2)
        : FilesService.getFileUrlBySize(this._school.head_cover_id, ImageSizeEnum.wide),
      title: this.title,
      link: this.schoolUrl,
      form: 'none',
    };
  }

  public doHoverImage() {
    this.doHover = !this.doHover;
  }

  public favoriteChanged(event) {
    this._school.visit_info.is_favorite = event;
  }
}
